import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import ReactCopyToClipboardModule from "react-copy-to-clipboard";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

const { CopyToClipboard: ReactCopyToClipboard } = ReactCopyToClipboardModule;

function extractBase64PngImage(input: string): string | null {
  const base64PngPattern = /^<img\s+[^>]*src\s*=\s*"data:image\/png;base64,([^"]+)"[^>]*>/;
  const match = base64PngPattern.exec(input);
  if (match?.[1]) {
    return match[1];
  }
  return null;
}

export default function CopyToClipboard({
  textToCopy,
  buttonText = "",
  icon: CustomIcon,
  variant = "ghost",
  onCopy,
}: {
  textToCopy: string;
  buttonText?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  variant?: "ghost" | "secondary" | "outline";
  onCopy?: () => void;
}) {
  const Icon = CustomIcon || ClipboardDocumentIcon;
  const [copied, setCopied] = useState(false);
  const [sanitizedText, setSanitizedText] = useState("");

  useEffect(
    () => setSanitizedText(DOMPurify.sanitize(textToCopy, { ALLOWED_TAGS: [] })),
    [textToCopy],
  );

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => {
        setCopied(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  });

  // NOTE: Hiding copy button for kernel generated images since easy workaround
  // exists for user, right click image and "copy".
  // TODO: Handle copy for kernel generated images as well
  if (extractBase64PngImage(textToCopy)) return null;

  return (
    <ReactCopyToClipboard
      text={sanitizedText}
      onCopy={() => {
        setCopied(true);
        onCopy?.();
      }}
    >
      <div>
        {copied ? (
          <Button
            size={buttonText ? "default" : "icon"}
            variant={variant}
            disabled
            className="text-green-700 pl-2 pr-2"
          >
            <ClipboardDocumentCheckIcon className={cn("h-5 w-5", { "mr-2": !!buttonText })} />
            {buttonText && "Copied!"}
          </Button>
        ) : (
          <Button
            size={buttonText ? "default" : "icon"}
            variant={variant}
            className="pl-2 pr-2"
          >
            <Icon
              className={cn("h-5 w-5", {
                "mr-2": !!buttonText,
              })}
            />{" "}
            {buttonText}
          </Button>
        )}
      </div>
    </ReactCopyToClipboard>
  );
}
